import React, { useEffect, useRef, useState } from 'react';
import { useObservationValue } from '../../hooks/useObservationValue';
import { ObservableLevelEnumValue } from '../../data/enums';
import { Select } from './Select';
import { Label } from './Label';

export type DropdownFieldValidation = '' | 'InvalidInput' | null;

interface DropdownFieldProps<T = string> {
  title?: string;
  titleType?: 'largeHeader' | 'subHeader' | 'body' | 'fieldTitle';
  placeholder: string;
  titleStyle?: string;
  options: T[];
  tooltipKey?: string;
  alwaysRunOnChange?: boolean;
  onChange?: (value: string) => void;
  onChangeNumberDifference?: (value: number, difference: number) => void;
  onDefaultLoad?: (value: string) => void;
  path: string;
  dataLevel: ObservableLevelEnumValue;
  entityId: number;
  entityExternalId: string;
  inspectionId: string;
  error?: string | null;
  inspectionCategoryTypeId: string;
  defaultValue?: string;
  value?: string;
  validationError?: DropdownFieldValidation;
}

const DropdownField = <T extends string = string>({
  title,
  titleType,
  placeholder,
  options,
  tooltipKey,
  titleStyle = `pr-8 text-primaryColor`,
  path,
  dataLevel,
  entityId,
  entityExternalId,
  inspectionId,
  error,
  inspectionCategoryTypeId,
  alwaysRunOnChange = false,
  onChange,
  onDefaultLoad,
  onChangeNumberDifference,
  defaultValue,
  value,
  validationError = null,
}: DropdownFieldProps<T>) => {
  const field = useObservationValue<string>({
    inspectionId,
    inspectionCategoryId: inspectionCategoryTypeId,
    dataLevel,
    entityId,
    entityExternalId,
    path,
    defaultValue,
    // onDefaultLoad is driven below, not via the hook: DropdownField's contract fires it with
    // '' even when nothing is found (the other atoms skip the call entirely in that case) -
    // a genuine pre-existing per-atom difference, preserved rather than unified away.
  });
  const selectedValue = field.value;

  const handleChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const oldValue = selectedValue;
    field.setValue(value);

    // success = true always in draft mode; in legacy mode it mirrors the real
    // upsertObservation outcome, so revert-on-failure only fires when it should.
    const success = await field.commit(value);
    if (!success && !alwaysRunOnChange) {
      field.setValue(oldValue);
    }

    if (success || alwaysRunOnChange) {
      onChange?.(value);
      if (onChangeNumberDifference) {
        const currentValue = parseInt(value);
        const previousValue = parseInt(oldValue);
        const difference = currentValue - previousValue;
        onChangeNumberDifference(
          currentValue,
          Number.isNaN(difference) ? currentValue : difference,
        );
      }
    }
  };

  // Fire onDefaultLoad exactly once, when the seed (persisted, or defaultValue, or '') lands.
  const [defaultLoadFired, setDefaultLoadFired] = useState(false);
  useEffect(() => {
    if (field.ready && !defaultLoadFired) {
      setDefaultLoadFired(true);
      onDefaultLoad?.(field.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.ready]);

  // Controlled external override. Legacy mode: setValue is a harmless local setState, so
  // always sync (including back to '' when the caller stops providing a value) - matches the
  // pre-hook contract exactly. Draft mode: setValue is a REAL write (marks dirty, schedules a
  // flush) - an uncontrolled field (no value prop) must NOT sync on mount, or it would dirty +
  // overwrite a real persisted selection with '' every time the screen loads.
  //
  // An EMPTY controlled value must not sync before a non-empty one has (#10549): a parent
  // that initialises its state to '' (TapeMeasure's number of shoes) would tombstone the
  // persisted selection on every mount - draft setValue('') routes to markDeleted, and the
  // seed then refuses to overwrite the dirty entry, so re-entering the screen wiped the
  // saved value. '' only counts as a deliberate clear once a real value has synced.
  const hasSyncedNonEmptyRef = useRef(false);
  useEffect(() => {
    if (field.isDraft) {
      if (value === undefined) {
        return;
      }
      if (value === '' && !hasSyncedNonEmptyRef.current) {
        return;
      }
    }
    if (value !== undefined && value !== '') {
      hasSyncedNonEmptyRef.current = true;
    }
    field.setValue(value ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (validationError === 'InvalidInput') {
      void field.remove(); // tombstone: removed on the next flush (draft) or now (legacy)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationError]);

  return (
    <div className="w-full space-y-2 py-2">
      {title && (
        <Label
          labelClassName={`${titleStyle}`}
          titleType={titleType ?? 'fieldTitle'}
          tooltipKey={tooltipKey}
        >
          {title}
        </Label>
      )}

      <Select
        options={options.map((o) => ({ label: o, value: o }))}
        placeholder={placeholder}
        onChange={handleChange}
        value={selectedValue}
      />

      {error && <div className="mt-2 text-sm text-red-500">{error}</div>}
    </div>
  );
};

export default DropdownField;
